import {useCallback, useEffect, useState} from 'react';

import {useNavigate} from 'react-router-dom';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {FeaturesEnum, HomeEnum, PlanTypeEnum, QueryParamsEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useGeolocation} from 'presentation/hooks/useGeolocation';
import {ErrorCode} from 'presentation/hooks/useGeolocation/types';
import {usePermissions} from 'presentation/hooks/usePermissions';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {translations} from 'presentation/translations-new';
import {getUserInfoFromToken} from 'presentation/utils';
import {useGuideStore} from 'presentation/modules/guide/shared/store/guideStore';
import {useGuideNavigation} from 'presentation/modules/guide/shared/hooks/useGuideNavigation/useGuideNavigation';
import {HandlePermissionsProps} from '../../types';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';
import {useGetCityLocationQuery} from 'presentation/modules/guide/shared/queries/useGetCityLocationQuery';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';

const {permissionsNotAllowed} = translations['pt-br'].commons;

export function useGuideLocation(flowType: PlanTypeEnum) {
	const {isLaptop} = useMediaQueries();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {isEnabled: isGeolocationEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_GEOLOC_GUIA,
	);
	const {isEnabled: isGeoOdontoEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_GEOLOC_GUIA_ODONTO,
	);
	const {isEnabled: isFavoritePlacesEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_FAVORITE_PLACES,
	);
	const navigate = useNavigate();
	const [openInstructionsState, setOpenInstructionsState] = useState(false);
	const [openInstabilityErrorState, setOpenInstabilityErrorState] = useState(false);
	const [showPermissionDeniedSnackbarState, setShowPermissionDeniedSnackbarState] =
		useState(false);
	const [getPositionClickedState, setGetPositionClickedState] = useState(0);
	const [openGetPositionLoading, setOpenGetPositionLoading] = useState(false);
	const resetHealthStates = useGuideStore(state => state.resetHealthStates);
	const resetOdontoStates = useGuideStore(state => state.resetOdontoStates);
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const resetBookingFlowStep = useBookingFlowStepsStore(state => state.resetBookingStep);
	const {step} = SEARCH_PARAMS;

	const {getPosition, resetCoords, coords, error: geolocationError} = useGeolocation();
	const {
		permission,
		checkPermission,
		error: permissionError,
	} = usePermissions({
		permissionName: 'geolocation',
	});

	const {handleNavToStateSelection, handleNavToHealthGuideFavoritePlaces} = useGuideNavigation({
		type: flowType,
	});

	useEffect(() => {
		resetBookingFlowStep();
		resetBookingData();
		checkPermission();
	}, []);

	useEffect(() => {
		setGetPositionClickedState(0);
	}, [permission]);

	useEffect(() => {
		if (permission === 'granted' && getPositionClickedState) {
			setOpenGetPositionLoading(true);
		}
	}, [permission, getPositionClickedState]);

	const {setCoordinates} = useGuideStore();
	const setLocation = useLocationStore(state => state.setLocation);

	const geolocationOrPermissionIsNotSupported =
		geolocationError?.code === ErrorCode.NOT_SUPPORTED ||
		permissionError === permissionsNotAllowed;

	const positionUnavailableOrTimeout =
		geolocationError?.code === ErrorCode.POSITION_UNAVAILABLE ||
		geolocationError?.code === ErrorCode.TIMEOUT;

	const permissionDenied =
		permission === 'denied' || geolocationError?.code === ErrorCode.PERMISSION_DENIED;

	const cdPessoaC = getUserInfoFromToken().cdPessoa;

	const {
		isLoading,
		error: cityLocationError,
		isError,
		refetch: fetchCityLocation,
		data: cityLocationData,
		isFetched,
	} = useGetCityLocationQuery({
		latitude: coords?.lat?.toString() || '',
		longitude: coords?.lng?.toString() || '',
	});

	const isUnprocessableError =
		(cityLocationError as APIError)?.statusCode === HttpStatusCode.unprocessable;

	const handleGoHome = useCallback(() => {
		navigate(HomeEnum.HOME_PAGE);
	}, [navigate]);

	const handleGetPosition = ({state}: HandlePermissionsProps) => {
		switch (state) {
			case 'prompt':
			case 'granted':
				setGetPositionClickedState(prev => prev + 1);
				resetCoords();
				getPosition();
				break;
			case 'denied':
				setOpenInstructionsState(true);
				break;
			default:
				break;
		}
	};

	const handleOnRetry = () => {
		setOpenInstabilityErrorState(false);
		fetchCityLocation();
	};

	const handleGetCityLocation = useCallback(() => {
		if (!cityLocationData || !('content' in cityLocationData) || !cityLocationData.content) {
			throw new Error('No city location found');
		}

		setLocation(
			cdPessoaC,
			flowType === PlanTypeEnum.HEALTH ? FlowType.HEALTH_GUIDE : FlowType.ODONTO_GUIDE,
			{
				state: {
					cdUf: cityLocationData.content.city.uf,
					nmUf: cityLocationData.content.city.state,
				},
				city: cityLocationData.content.city.name,
			},
		);

		setSearchParams(prev => {
			prev.set(QueryParamsEnum.UF_CODE, cityLocationData.content.city.uf);
			prev.set(QueryParamsEnum.UF_NAME, cityLocationData.content.city.state);
			prev.set(QueryParamsEnum.CITY_NAME, cityLocationData.content.city.name);
			prev.set(QueryParamsEnum.STEP, GuideStep.DETAILS);
			return prev;
		});
	}, [fetchCityLocation, setLocation, cdPessoaC, flowType, cityLocationData]);

	const initializeGeoGuideFlow = () => {
		if (flowType === PlanTypeEnum.ODONTO) {
			resetOdontoStates();
		}

		if (flowType !== PlanTypeEnum.ODONTO) {
			resetHealthStates();
		}
		handleGetPosition({state: permission});
	};

	useEffect(() => {
		const shouldShowInstabilityError =
			positionUnavailableOrTimeout || geolocationOrPermissionIsNotSupported || isError;

		if (coords) {
			setCoordinates(coords);
			setSearchParams(prev => {
				prev.set(QueryParamsEnum.COORDINATES, `${coords.lat},${coords.lng}`);
				return prev;
			});
		}

		if (permissionDenied && getPositionClickedState) {
			setShowPermissionDeniedSnackbarState(true);
		}

		if (shouldShowInstabilityError) {
			setOpenInstabilityErrorState(true);
		}
	}, [
		coords,
		permissionDenied,
		getPositionClickedState,
		positionUnavailableOrTimeout,
		geolocationOrPermissionIsNotSupported,
		isError,
		isLoading,
	]);

	useEffect(() => {
		if (!step) {
			setSearchParams(prev => {
				prev.set(QueryParamsEnum.STEP, GuideStep.LOCATION);
				return prev;
			});
		}

		if (isFetched && cityLocationData) {
			handleGetCityLocation();
		}
	}, [isFetched, cityLocationData, step]);

	const isGeoEnabled =
		(flowType === PlanTypeEnum.ODONTO && isGeoOdontoEnabled) ||
		(flowType === PlanTypeEnum.HEALTH && isGeolocationEnabled);

	return {
		isLaptop,
		isGeoEnabled,
		permission,
		openInstructionsState,
		setOpenInstructionsState,
		openInstabilityErrorState,
		setOpenInstabilityErrorState,
		showPermissionDeniedSnackbarState,
		setShowPermissionDeniedSnackbarState,
		openGetPositionLoading,
		handleNavToStateSelection,
		handleNavToHealthGuideFavoritePlaces,
		initializeGeoGuideFlow,
		positionUnavailableOrTimeout,
		geolocationOrPermissionIsNotSupported,
		isUnprocessableError,
		handleOnRetry,
		handleGoHome,
		isFavoritePlacesEnabled,
	};
}
